import { EWorkshopChildcareAgeGroups } from '@packages/utils';
import {
  EAgeGroup,
  EWorkshopLanguage,
  TAddressShortDataDB,
  TAddressShortDataUI,
  TAddressTypesDB,
  TAddressTypesUI,
  TAllowedOperationsDB,
  TAllowedOperationsUI,
  TAllowedOperationsValueDB,
  TAllowedOperationsValueUI,
  TLanguageOptionUI,
  TShortAddressWithAllowOperationsDB,
  TShortAddressWithAllowOperationsUI,
  EDateSortParams,
} from './generic.types';
import { TTagDB, TTagUI } from './tags.types';

export enum EWorkshopStatus {
  PLANNING = 'PLANNING',
  PUBLISHED = 'PUBLISHED',
  CANCELLED = 'CANCELLED',
  CLOSED = 'CLOSED',
  RESCHEDULED = 'RESCHEDULED',
}

export enum EWorkshopCategory {
  STANDALONE = 'STANDALONE',
  SERIES_WITH_THREE_PARTS = 'SERIES_WITH_THREE_PARTS',
  SERIES_WITH_TWO_PARTS = 'SERIES_WITH_TWO_PARTS',
}

export enum EAttendanceType {
  IN_PERSON = 'IN_PERSON',
  VIRTUAL = 'VIRTUAL',
}

export enum EWorkshopPartType {
  STANDALONE = 'STANDALONE',
  PART_ONE = 'PART_ONE',
  PART_TWO = 'PART_TWO',
  PART_THREE = 'PART_THREE',
}

export enum EWorkshopStandaloneType {
  READY_FOR_MATH = 'READY_FOR_MATH',
  READY_FOR_KINDERGARTEN_SOCIAL_EMOTIONAL = 'READY_FOR_KINDERGARTEN_SOCIAL_EMOTIONAL',
  CHILD_CARE_AND_PRESCHOOL_PROGRAMS_PART_1 = 'CHILD_CARE_AND_PRESCHOOL_PROGRAMS_PART_1',
  CHILD_CARE_AND_PRESCHOOL_PROGRAMS_PART_2 = 'CHILD_CARE_AND_PRESCHOOL_PROGRAMS_PART_2',
}

export enum EWorkshopSeriesType {
  READY_FOR_KINDERGARTEN = 'READY_FOR_KINDERGARTEN',
  LEARNING_CHILD_CARE_AND_PRESCHOOL_PROGRAMS = 'LEARNING_CHILD_CARE_AND_PRESCHOOL_PROGRAMS',
}

export enum EWorkshopSortParams {
  CREATION_DATE = 'CREATION_DATE',
  // Shadow fields for the Session Date column (backend WorkshopSortFiled)
  NEXT_SESSION_DATE = EDateSortParams.NEXT_SESSION_DATE,
  LAST_SESSION_DATE = EDateSortParams.LAST_SESSION_DATE,
}
export enum EWorkshopFilterParams {
  STATUS = 'statuses',
  TYPE = 'types',
  HOST = 'hostIds',
  CITY = 'cities',
  LOCATION = 'locationIds',
  TAGS = 'tagIds',
  HOSTING_TYPES = 'hostingTypes',
  ATTENDANCE_TYPE = 'attendanceTypes',
  SITE_COORDINATOR = 'siteCoordinator',
  ELC = 'elcIds',
  CAPACITY = 'capacity',
  NEAR_CAPACITY = 'nearCapacity',
  OVER_CAPACITY = 'overCapacity',
}

// Sentinel value used in the ELC filter dropdown to represent the
// "Not in an ELC" option. The server expects `includeNoElc: true`
// rather than a real UUID, so we strip this sentinel out of `elcIds`
// in the filter converter (see workshopFilterConverter).
export const WORKSHOP_NO_ELC_FILTER_VALUE = '__NO_ELC__';

// Body of POST /workshops/export (GROW-4161): the same filters as the list
// GET, plus the visible column ids and the active sort.
export type TExportWorkshopsFilters = {
  workshopIds?: string[];
  hostIds?: string[];
  elcIds?: string[];
  includeNoElc?: boolean;
  locationIds?: string[];
  cities?: string[];
  types?: string[];
  attendanceTypes?: string[];
  statuses?: string[];
  hostingTypes?: string[];
  siteCoordinator?: string;
  tagIds?: string[];
  nearCapacity?: boolean;
  overCapacity?: boolean;
};

export type TExportWorkshopsRequest = {
  columns?: string[];
  filters: TExportWorkshopsFilters;
  sort?: {
    field: string;
    direction: string;
  };
};

export enum ECapacityEmptyTexts {
  UNLIMITED_LONG = 'UNLIMITED_LONG',
  UNLIMITED_SHORT = 'UNLIMITED_SHORT',
  LIMITED_WITH_WORKSHOP = 'LIMITED_WITH_WORKSHOP',
}

export type TWorkshopHostDataUI = {
  id: string;
  name: string;
  address: TAddressShortDataUI;
  allowedOperations: TAllowedOperationsUI;
  hostType?: 'ORGANIZATION' | 'ELC';
};
export type TWorkshopHostDataDB = {
  id: string;
  name: string;
  address: TAddressShortDataDB;
  allowedOperations: TAllowedOperationsDB;
  hostType?: 'ORGANIZATION' | 'ELC';
};

export type TWorkshopLocationUI = {
  id: string;
  name: string;
  address: TAddressShortDataUI;
  nameAndAddress: string;
  allowedOperations: TAllowedOperationsUI;
};
export type TWorkshopLocationDB = {
  id: string;
  name: string;
  address: TAddressShortDataDB;
  allowedOperations: TAllowedOperationsDB;
};

export type TWorkshopSiteCoordinatorUI = {
  allowedOperations: TAllowedOperationsUI;
  email: string;
  firstName: string;
  id: string;
  lastName: string;
  phone?: string;
  profilePicture?: string;
  fullName?: string;
};
export type TWorkshopSiteCoordinatorDB = {
  allowedOperations: TAllowedOperationsDB;
  email: string;
  firstName: string;
  id: string;
  lastName: string;
  phone?: string;
  profilePicture?: string;
};

export type TPhotoGalleryUI = {
  originalPhoto: string;
  thumbnailPhoto: string;
};

export type TPhotoGalleryDB = {
  originalPhoto: string;
  thumbnailPhoto: string;
};

export type TWorkshopGalleryUI = {
  photos: TPhotoGalleryUI[];
};

export type TWorkshopGalleryDB = {
  photos: TPhotoGalleryDB[];
};

export type TAddWorkshopPhotoUI = {
  workshopId: string;
  photoUrl: string;
};

export type TAddWorkshopPhotoDB = {
  workshopId: string;
  photoUrl: string;
};

export type TWorkshopRegistrantsUrl = {
  workshopId: string;
  localDate: string;
};

export type TSessionsUI = {
  facilitator: TWorkshopSiteCoordinatorUI;
  id: string;
  partType: EWorkshopPartType;
};

export type TSessionsDB = {
  facilitator: TWorkshopSiteCoordinatorDB;
  id: string;
  partType: EWorkshopPartType;
};

export type TCapacityUI = {
  allowedOperations: TAllowedOperationsUI;
  value: string;
  showValue?: string;
};
export type TCapacityDB = {
  allowedOperations: TAllowedOperationsDB;
  value: string;
};

export type TClassItemUI = {
  ageGroup: TAgeGroupAllowedOperationsValueUI;
  capacity: TCapacityUI;
  id: string;
  language: TLanguageAllowedOperationsValueUI;
  sessions: TSessionsUI[];
  /** Confirmed registrants of this class */
  registrantsCount: number;
  /** Registrants currently on this class's waitlist */
  waitlistCount: number;
  /**
   * "Registered" for the session card Attendance badge (GROW-4302, SRD 1.2):
   * confirmed registrants PLUS the waitlist, on purpose, so admins can see
   * over-capacity at a glance. Do not narrow this back to registrantsCount.
   */
  registeredCount: number;
  /** Registrants with at least one part marked attended (GROW-4303); null until tallied */
  attendeesCount: number | null;
};

export type TClassItemDB = {
  ageGroup: TAgeGroupAllowedOperationsValueDB;
  capacity: TCapacityDB;
  id: string;
  language: TLanguageAllowedOperationsValueDB;
  sessions: TSessionsDB[];
  registrantsCount: number;
  /** GROW-4303: optional until the backend ships them */
  waitlistCount?: number;
  attendeesCount?: number | null;
};

export type TClassesDataUI = {
  allowedOperations: TAllowedOperationsUI;
  value?: TClassItemUI[];
};

export type TClassesDataDB = {
  allowedOperations: TAllowedOperationsDB;
  value?: TClassItemDB[];
};

export type TPossibleAgeGroupUI = {
  value: EAgeGroup;
  label: EAgeGroup;
};

export type TFlyersDataUI = {
  existingAgeGroups: EAgeGroup[];
  parts: TPartsUI[];
  timezone: TWorkshopTimezone | null;
};

export enum EWorkshopDetailsAllowedActions {
  // actions came from BE
  DELETE = 'DELETE',
  CLOSE = 'CLOSE',
  PUBLISH = 'PUBLISH',
  DUPLICATE = 'DUPLICATE',
  CANCEL = 'CANCEL',
  RESCHEDULE = 'RESCHEDULE',
  REPUBLISH = 'REPUBLISH',
  // actions added on FE
  DOWNLOAD_FLYER = 'DOWNLOAD_FLYER',
  DOWNLOAD_LANDING_QR = 'DOWNLOAD_LANDING_QR',
  COPY_URL = 'COPY_URL',
}

export type TPartsUI = {
  date?: TAllowedOperationsValueUI<string> | null;
  isPassed: boolean;
  partType: EWorkshopPartType;
  workshopPartId: string;
  startTime?: string;
  endTime?: string;
};

export type TPartsDB = {
  date?: TAllowedOperationsValueDB<string> | null;
  isPassed: boolean;
  partType: EWorkshopPartType;
  workshopPartId: string;
  startTime?: string;
  endTime?: string;
};

export type TWorkshopDetailsDataUI = {
  actualSessionDates: string[];
  allowedActions: EWorkshopDetailsAllowedActions[];
  attendanceType: EAttendanceType;
  capacity: TCapacityUI;
  childcareProvided: TAllowedOperationsValueUI<boolean> | null;
  classes: TClassesDataUI;
  customQuestion: TAllowedOperationsValueUI<string>;
  description?: TAllowedOperationsValueUI<string>;
  foodProvided: TAllowedOperationsValueUI<boolean>;
  host: TWorkshopHostDataUI;
  hostedPrivately: boolean;
  id: string;
  location: TWorkshopLocationUI;
  locationSameAsHost: boolean;
  meetingLink: TAllowedOperationsValueUI<string> | null;
  photo: TAllowedOperationsValueUI<string>;
  possibleAgeGroups: EAgeGroup[];
  shipmentAddress: TShortAddressWithAllowOperationsUI;
  shipmentLocation: TWorkshopLocationUI;
  shipmentAddressSameAsLocationAddress: boolean;
  siteCoordinators: TWorkshopSiteCoordinatorUI[];
  siteCoordinatorsAllowedOperations: TAllowedOperationsUI;
  eventCoordinators: TWorkshopSiteCoordinatorUI[];
  eventCoordinatorsAllowedOperations: TAllowedOperationsUI;
  status: EWorkshopStatus;
  type: EWorkshopStandaloneType | EWorkshopSeriesType;
  typeCategory: EWorkshopCategory;
  hasStartedPart: boolean;
  timezone: TWorkshopTimezone | null;
  parts?: TPartsUI[];
  flyerData: TFlyersDataUI;
  isCCP: boolean;
  allowRegistration?: boolean;
};

export type TWorkshopDetailsDataDB = {
  actualSessionDates: string[];
  allowedActions: EWorkshopDetailsAllowedActions[];
  attendanceType: EAttendanceType;
  capacity: TCapacityDB;
  childcareProvided: TAllowedOperationsValueDB<boolean>;
  classes: TClassesDataDB;
  customQuestion: TAllowedOperationsValueDB<string>;
  description?: TAllowedOperationsValueDB<string>;
  foodProvided: TAllowedOperationsValueDB<boolean>;
  host: TWorkshopHostDataDB;
  hostedPrivately: boolean;
  id: string;
  location: TWorkshopLocationDB;
  locationSameAsHost: boolean;
  meetingLink: TAllowedOperationsValueDB<string> | null;
  photo: TAllowedOperationsValueDB<string>;
  possibleAgeGroups: EAgeGroup[];
  shipmentAddress: TShortAddressWithAllowOperationsDB;
  shipmentLocation: TWorkshopLocationDB;
  shipmentAddressSameAsLocationAddress: boolean;
  siteCoordinators: {
    value: TWorkshopSiteCoordinatorDB[];
    allowedOperations?: TAllowedOperationsDB;
  };
  eventCoordinators: {
    value: TWorkshopSiteCoordinatorDB[];
    allowedOperations?: TAllowedOperationsDB;
  };
  status: EWorkshopStatus;
  type: EWorkshopStandaloneType | EWorkshopSeriesType;
  typeCategory: EWorkshopCategory;
  hasStartedPart: boolean;
  timezone: TWorkshopTimezone | null;
  parts: TPartsDB[];
  allowRegistration?: boolean;
};

export type TWorkshopChildcareRequirementUI = {
  ageGroup: EWorkshopChildcareAgeGroups;
  childCount: number;
};

// GROW-4362: minimal admin registration. Only first/last name (+ session) are
// required; every other contact field is optional and omitted when blank.
export type TAddWorkshopRegistrationUI = {
  workshopId: string;
  workshopClassId: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  customQuestionAnswer?: string;
  childNames?: string[];
  childcareRequirements?: TWorkshopChildcareRequirementUI[];
  // `false` for READY! for Kindergarten workshops, `undefined` everywhere else.
  hasReadyBinder?: boolean;
};

// Mirrors AddRegistrationToWorkshopCommand. `materialsLanguage` is never sent
// (the backend defaults minimal registrations to ENGLISH) and
// `allowedTakePicture` is always `false`.
export type TAddWorkshopRegistrationDB = {
  workshopId: string;
  workshopClassId: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  allowedTakePicture: false;
  customQuestionAnswer?: string;
  childNames?: string[];
  childcareRequirements?: TWorkshopChildcareRequirementUI[];
  hasReadyBinder?: boolean;
};

export type TWorkshopChildCareUI = {
  ageGroup: EWorkshopChildcareAgeGroups;
  count: string;
};

export type TWorkshopChildCareDB = {
  ageGroup: EWorkshopChildcareAgeGroups;
  count: string;
};

export type TWorkshopSessionDataUI = {
  partType: EWorkshopPartType;
  date: string;
  facilitator: string;
};

export type TWorkshopSessionDataDB = {
  partType: EWorkshopPartType;
  dateTime?: string;
  facilitatorId?: string;
};

export type TWorkshopClassDataUI = {
  workshopId: string;
  age: EAgeGroup;
  language: EWorkshopLanguage;
  capacity?: string;
  sessions: TWorkshopSessionDataUI[];
};

export type TWorkshopClassDataDB = {
  workshopId: string;
  ageGroup: EAgeGroup;
  language: EWorkshopLanguage;
  capacity?: string;
  sessions: TWorkshopSessionDataDB[];
};

export type TDuplicateWorkshopUI = {
  workshopId: string;
};

export type TDuplicateWorkshopDB = {
  workshopId: string;
};

export type TDeleteWorkshopClassUI = {
  classId: string;
  workshopId: string;
};

export type TDeleteWorkshopClassDB = {
  classId: string;
  workshopId: string;
  description?: TAllowedOperationsValueDB<string>;
};

export type TWorkshopCreateUI = {
  type: EWorkshopSeriesType | EWorkshopStandaloneType;
  host: string;
  hostType: string;
  hostedPrivately: boolean;
  attendanceType: EAttendanceType;
};

export type TWorkshopCreateDB = {
  type: EWorkshopSeriesType | EWorkshopStandaloneType;
  hostId?: string;
  elcId?: string;
  hostedPrivately: boolean;
  attendanceType: EAttendanceType;
};

export type TWorkshopSessionItemUI = {
  ageGroup: EAgeGroup;
  language: EWorkshopLanguage;
  count: number;
};
export type TWorkshopSessionItemDB = {
  ageGroup: EAgeGroup;
  language: EWorkshopLanguage;
  count: number;
};

export type TWorkshopSessionDateUI = {
  partType: EWorkshopPartType;
  date: string | null;
};
export type TWorkshopSessionDateDB = Record<EWorkshopPartType, string | null>;

export type TWorkshopDataUI = {
  workshopId: string;
  hostName: string;
  hostId: string;
  hostedPrivately: boolean;
  attendanceType: EAttendanceType;
  createdDate: string;
  city: string;
  workshopType: EWorkshopStandaloneType | EWorkshopSeriesType;
  workshopStatus: EWorkshopStatus;
  workshopCategory: EWorkshopCategory;
  englishSessions?: TWorkshopSessionItemUI[];
  spanishSessions?: TWorkshopSessionItemUI[];
  tags?: TTagUI[];
  locationId: string;
  locationName: string;
  capacity: string | null;
  registrantCount: number;
  attendanceCount: number;
  hasWaitList: boolean;
  sessionDates: TWorkshopSessionDateUI[];
  /** Part holding the next upcoming session (GROW-4274); null once all are past */
  nextSessionPart?: EWorkshopPartType | null;
  siteCoordinators?: string[];
  elcs: TWorkshopLocationElcUI[];
};
export type TWorkshopLocationElcUI = {
  id: string;
  name: string;
};
export type TWorkshopDataDB = {
  workshopId: string;
  hostName: string;
  hostId: string;
  hostedPrivately: boolean;
  attendanceType: EAttendanceType;
  createdDate: string;
  city: string;
  workshopType: EWorkshopStandaloneType | EWorkshopSeriesType;
  workshopStatus: EWorkshopStatus;
  workshopCategory: EWorkshopCategory;
  englishSessions?: TWorkshopSessionItemDB[];
  spanishSessions?: TWorkshopSessionItemDB[];
  tags?: TTagDB[];
  locationId: string;
  locationName: string;
  capacity: string | null;
  registrantCount: number;
  attendanceCount: number;
  hasWaitList: boolean;
  sessionDates: TWorkshopSessionDateDB;
  nextSessionPart?: EWorkshopPartType | null;
  siteCoordinators?: string[];
  elcs: TWorkshopLocationElcDB[];
};
export type TWorkshopLocationElcDB = {
  id: string;
  name: string;
};

export type TAgeGroupAllowedOperationsValueUI = {
  allowedOperations: TAllowedOperationsUI;
  value: TPossibleAgeGroupUI;
};

export type TAgeGroupAllowedOperationsValueDB = {
  allowedOperations: TAllowedOperationsDB;
  value: EAgeGroup;
};

export type TLanguageAllowedOperationsValueUI = {
  allowedOperations: TAllowedOperationsUI;
  value: TLanguageOptionUI;
};

export type TLanguageAllowedOperationsValueDB = {
  allowedOperations: TAllowedOperationsDB;
  value: EWorkshopLanguage;
};

export type TWorkshopClassAgeGroupEditUI = {
  id: string;
  workshopId: string;
  value: string;
};
export type TWorkshopClassAgeGroupEditDB = {
  workshopClassId: string;
  workshopId: string;
  ageGroup: string;
};

export type TWorkshopClassLanguageEditUI = {
  id: string;
  workshopId: string;
  value: EWorkshopLanguage;
};
export type TWorkshopClassLanguageEditDB = {
  workshopClassId: string;
  workshopId: string;
  language: EWorkshopLanguage;
};

export type TWorkshopClassDateTimeEditUI = {
  id: string;
  workshopId: string;
  workshopSessionId: string;
  value: string;
};
export type TWorkshopClassDateTimeEditDB = {
  workshopClassId: string;
  workshopId: string;
  workshopSessionId: string;
  dateTime: string;
};
export type TWorkshopPartDateEditDB = {
  workshopId: string;
  workshopPartId: string;
  date: string;
};
export type TWorkshopPartDateEditUI = {
  workshopId: string;
  workshopPartId: string;
  dateTime: string;
};
export type TWorkshopPartTimeEditDB = {
  workshopId: string;
  workshopPartId: string;
  time: string;
  timeType: 'START_TIME' | 'END_TIME';
};
export type TWorkshopPartTimeEditUI = {
  workshopId: string;
  workshopPartId: string;
  time: string;
  timeType: 'START_TIME' | 'END_TIME';
};
export type TWorkshopTimezone = 'MT' | 'PT';
export type TWorkshopTimezoneEditUI = {
  workshopId: string;
  timezone: TWorkshopTimezone;
};
export type TWorkshopTimezoneEditDB = {
  workshopId: string;
  timezone: TWorkshopTimezone;
};
export type TWorkshopClassFacilitatorEditUI = {
  id: string;
  workshopId: string;
  workshopSessionId: string;
  value: string | null;
};

export type TWorkshopClassFacilitatorEditDB = {
  workshopClassId: string;
  workshopId: string;
  workshopSessionId: string;
  userId: string | null;
};

export type TClassCapacityEditUI = {
  id: string;
  workshopId: string;
  value: string;
};
export type TClassCapacityEditDB = {
  workshopClassId: string;
  workshopId: string;
  capacity: string;
};

export type TWorkshopCapacityEditUI = {
  workshopId: string;
  value: string;
};
export type TWorkshopCapacityEditDB = {
  workshopId: string;
  capacity: string | null;
};

export type TWorkshopLocationEditUI = {
  id: string;
  value: string;
};
export type TWorkshopLocationEditDB = {
  workshopId: string;
  locationId: string;
};
export type TWorkshopHostEditUI = {
  id: string;
  value: string | null;
};
export type TWorkshopHostEditDB = {
  workshopId: string;
  hostId: string | null;
};

export type TWorkshopSiteCoordinatorEditUI = {
  id: string;
  value: string[];
};

export type TWorkshopSiteCoordinatorEditDB = {
  workshopId: string;
  userIds: string[];
};

export type TWorkshopCoordinatorEditUI = {
  id: string;
  value: string[];
};

export type TWorkshopCoordinatorEditDB = {
  workshopId: string;
  userIds: string[];
};

export type TWorkshopFoodProvidedEditUI = {
  id: string;
  value: boolean;
};
export type TWorkshopFoodProvidedEditDB = {
  workshopId: string;
  foodProvided: boolean;
};

export type TWorkshopMeetingLinkEditUI = {
  id: string;
  value: string | null;
};
export type TWorkshopMeetingLinkEditDB = {
  workshopId: string;
  meetingLink: string | null;
};

export type TWorkshopChildcareProvidedEditUI = {
  id: string;
  value: boolean;
};
export type TWorkshopChildcareProvidedEditDB = {
  workshopId: string;
  childcareProvided: boolean;
};

export type TWorkshopShippingAddressEditUI = TAddressTypesUI & {
  id: string;
};
export type TWorkshopShippingAddressEditDB = {
  workshopId: string;
  address: TAddressTypesDB;
};

export type TWorkshopLocationAddressEditUI = {
  workshopId: string;
  address: TAddressTypesUI;
};
export type TWorkshopLocationAddressEditDB = {
  workshopId: string;
  address: TAddressTypesDB;
};

export type TWorkshopShipmentLocationEditUI = {
  id: string;
  value: string;
};
export type TWorkshopShipmentLocationEditDB = {
  workshopId: string;
  locationId: string;
};

export type TWorkshopDescriptionEditUI = {
  id: string;
  value: string;
};
export type TWorkshopDescriptionEditDB = {
  workshopId: string;
  description: string;
};

export type TUpdateCustomQuestionUi = { id: string; question: string };

export type TWorkshopsNoteUI = {
  workshopId: string;
  noteId?: string;
  note?: string;
};

export type TWorkshopsNoteDB = {
  workshopId: string;
  noteId?: string;
  note?: string;
};

export type TWorkshopPartKitOrderItemUI = {
  kitId: string;
  name: string;
  inventory?: number;
  suggestedOrder: number;
  totalOrders: number;
  orderDate: string | null;
  kitOrderId?: string | null;
};

export type TKitShipmentLocationUI = {
  id: string;
  name: string;
  address: TAddressShortDataUI;
};

export type TWorkshopPartKitOrdersUI = {
  workshopId: string;
  items: Record<EWorkshopPartType, TWorkshopPartKitOrderItemUI[]>;
  shipmentLocation?: TKitShipmentLocationUI;
};

export type TWorkshopPartKitOrderItemDB = {
  kitId: string;
  name: string;
  inventory?: number;
  suggestedOrder: number;
  totalOrders: number;
  orderDate: string | null;
  kitOrderId?: string | null;
};

export type TKitShipmentLocationDB = {
  id: string;
  name: string;
  address: TAddressShortDataDB;
};

export type TWorkshopPartKitOrdersDB = {
  workshopId: string;
  items: Record<string, TWorkshopPartKitOrderItemDB[]>;
  shipmentLocation?: TKitShipmentLocationDB;
};

export type TWorkshopKitOrderHistoryItem = {
  workshopKitId: string;
  kitName: string;
  orderCount: number;
  orderedBy: string;
  orderedByName: string;
  orderDate: string;
  orderChange: number;
  operation: string;
};

export type TWorkshopKitOrderHistoryResponse = {
  orderHistory: {
    [key in EWorkshopPartType]?: {
      [date: string]: TWorkshopKitOrderHistoryItem[];
    };
  };
};
